/**
 * CAESection.jsx — iter197p
 * =========================
 * Front-of-House review/edit panel for the Customer Advice Engine summary
 * attached to a job group. Mounted inside the per-JG CustomerCommunication
 * stack on the pricing screen.
 *
 * Reads:   jobGroup.customer_advice  (set by /cae/generate-all or by the
 *          pre-send backfill on /yestimate/{id}/send)
 *
 * Writes:  POST /api/foh/yestimate/{ye_id}/job-group/{jg_id}/cae/regenerate
 *          PUT  /api/foh/yestimate/{ye_id}/job-group/{jg_id}/cae
 *
 * Design notes
 *   • The card always shows the current summary (with category chip + word
 *     count + AI-vs-edited badge). FOH can collapse the expanded paragraph.
 *   • Regenerate fires a fresh LLM call; existing audit trail is preserved.
 *   • Edit mode reveals two textareas — summary first, expanded below.
 *     On save the version bumps and `edited=true` is stamped.
 *   • Errors are toasted; the JG-level handler refreshes the cached state.
 */
import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Pencil, RefreshCw, Save, X, ChevronDown, ChevronUp, MessageSquareText, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { API } from "@/contexts/AuthContext";

const CATEGORY_COLOURS = {
  safety:           "bg-red-100 text-red-800 border-red-300",
  reliability:      "bg-amber-100 text-amber-800 border-amber-300",
  preventative:     "bg-emerald-100 text-emerald-800 border-emerald-300",
  mechanical_risk:  "bg-orange-100 text-orange-800 border-orange-300",
  emissions:        "bg-sky-100 text-sky-800 border-sky-300",
  diagnostics:      "bg-indigo-100 text-indigo-800 border-indigo-300",
  generic:          "bg-slate-100 text-slate-800 border-slate-300",
};

function wordCount(s) {
  return ((s || "").match(/\b\w+\b/g) || []).length;
}

export default function CAESection({ jobGroup, yestimateId, onApplied }) {
  const cae = jobGroup?.customer_advice;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showExpanded, setShowExpanded] = useState(false);
  const [editSummary, setEditSummary] = useState(cae?.summary || "");
  const [editExpanded, setEditExpanded] = useState(cae?.expanded || "");

  const jgId = jobGroup?.id;
  if (!jgId) return null;

  const refresh = async () => {
    if (typeof onApplied === "function") {
      await onApplied();
    }
  };

  const handleRegenerate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/foh/yestimate/${yestimateId}/job-group/${jgId}/cae/regenerate`
      );
      toast.success("Customer advice regenerated");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to regenerate advice");
    } finally {
      setBusy(false);
    }
  };

  const handleStartEdit = () => {
    setEditSummary(cae?.summary || "");
    setEditExpanded(cae?.expanded || "");
    setEditing(true);
  };

  // iter198xx — Delete the customer advice for this JG entirely. FOH
  // confirmation is intentionally a single confirm() prompt rather
  // than a modal: the action is undoable via Regenerate (no data
  // loss, the audit trail is preserved), so we trade a heavier UX
  // for speed.
  const handleDelete = async () => {
    if (busy) return;
    if (!window.confirm(
      "Remove the customer advice for this job?\n\n"
      + "Customers won't see any AI-written paragraph for this item. "
      + "You can hit Regenerate later if you change your mind."
    )) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API}/foh/yestimate/${yestimateId}/job-group/${jgId}/cae`
      );
      toast.success("Customer advice removed");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove advice");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async () => {
    if (busy) return;
    if (!editSummary.trim() || editSummary.trim().length < 10) {
      toast.error("Summary must be at least 10 characters");
      return;
    }
    setBusy(true);
    try {
      await axios.put(
        `${API}/foh/yestimate/${yestimateId}/job-group/${jgId}/cae`,
        { summary: editSummary.trim(), expanded: editExpanded.trim() }
      );
      toast.success("Customer advice saved");
      setEditing(false);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  // Header chip: category + AI/edited + word count
  const cat = cae?.category || "generic";
  const catCls = CATEGORY_COLOURS[cat] || CATEGORY_COLOURS.generic;
  const isAi = cae?.generation_source === "ai";
  const isFallback = cae?.generation_source === "fallback";
  const isEdited = !!cae?.edited;

  // iter199aa — Render the "Generating customer advice…" state when
  // the backend has scheduled a CAE backfill for this JG. The sentinel
  // is written by `services/cae/auto_backfill.py` and cleared once the
  // LLM call resolves. Without this branch FOH would just see the
  // "No advice yet — click Regenerate" empty state and assume the AI
  // pass had failed.
  const isGenerating = !!cae?.generating && !cae?.summary;
  const hasError = !!cae?.error && !cae?.summary;

  return (
    <div
      className="border border-slate-200 rounded-lg overflow-hidden mt-3"
      data-testid={`cae-section-${jgId}`}
    >
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <MessageSquareText className="w-4 h-4 text-slate-600" />
        <span className="text-sm font-semibold text-slate-700">Customer advice</span>
        {isGenerating && (
          <Badge variant="outline" className="text-[10px] border-indigo-300 text-indigo-700" data-testid={`cae-generating-${jgId}`}>
            <RefreshCw className="w-2.5 h-2.5 mr-0.5 animate-spin" /> Generating…
          </Badge>
        )}
        {hasError && (
          <Badge variant="outline" className="text-[10px] border-red-300 text-red-700" data-testid={`cae-error-${jgId}`}>
            Generation failed — click Regenerate
          </Badge>
        )}
        {cae?.category_label && !isGenerating && (
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${catCls}`} data-testid={`cae-category-chip-${jgId}`}>
            {cae.category_label}
          </span>
        )}
        {isAi && (
          <Badge variant="outline" className="text-[10px] border-indigo-300 text-indigo-700" data-testid={`cae-source-ai-${jgId}`}>
            <Sparkles className="w-2.5 h-2.5 mr-0.5" /> AI-generated
          </Badge>
        )}
        {isFallback && (
          <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600" data-testid={`cae-source-fallback-${jgId}`}>
            Fallback copy
          </Badge>
        )}
        {isEdited && (
          <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-800" data-testid={`cae-source-edited-${jgId}`}>
            <Pencil className="w-2.5 h-2.5 mr-0.5" /> FOH-edited
          </Badge>
        )}
        {cae?.word_count_summary != null && (
          <span className="text-[10px] text-slate-500 ml-auto" data-testid={`cae-word-count-${jgId}`}>
            {cae.word_count_summary} words
          </span>
        )}
      </div>

      <div className="px-3 py-3 bg-white">
        {/* iter199aa — In-flight pass kicked off by the backend
            auto-backfill or by Auto-generate. Show a clear pending
            block (instead of the "no advice yet" empty state) so FOH
            knows the page is busy. The Regenerate button is
            disabled while in flight to avoid double work. */}
        {isGenerating && (
          <div
            className="text-sm text-slate-600 italic flex items-center gap-2 py-2"
            data-testid={`cae-generating-body-${jgId}`}
          >
            <RefreshCw className="w-3.5 h-3.5 text-indigo-500 animate-spin" />
            Generating customer advice…
          </div>
        )}

        {/* iter198xx — Show a distinct "deliberately removed" state
            so FOH knows the empty paragraph is intentional, not a
            generation miss. Regenerate is always one click away. */}
        {cae?.deleted && (
          <div
            className="text-sm text-slate-500 italic mb-2 flex items-center gap-1.5"
            data-testid={`cae-deleted-${jgId}`}
          >
            <Trash2 className="w-3.5 h-3.5 text-slate-400" />
            Customer advice removed by FOH — the portal will show no AI paragraph for this job. Click Regenerate to bring it back.
          </div>
        )}

        {!cae?.summary && !cae?.deleted && !isGenerating && !hasError && (
          <div className="text-sm text-slate-500 italic mb-2" data-testid={`cae-empty-${jgId}`}>
            No customer advice yet — click Regenerate to build one.
          </div>
        )}

        {hasError && (
          <div className="text-sm text-red-700 italic mb-2" data-testid={`cae-error-body-${jgId}`}>
            We couldn&apos;t generate this advice automatically. Click Regenerate to try again.
          </div>
        )}

        {/* Display mode */}
        {!editing && cae?.summary && (
          <>
            <p className="text-sm text-slate-800 leading-relaxed" data-testid={`cae-summary-${jgId}`}>
              {cae.summary}
            </p>
            {cae.expanded && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowExpanded(v => !v)}
                  className="text-xs text-indigo-700 hover:text-indigo-900 flex items-center gap-1"
                  data-testid={`cae-expand-toggle-${jgId}`}
                >
                  {showExpanded ? (
                    <><ChevronUp className="w-3 h-3" /> Hide expanded detail</>
                  ) : (
                    <><ChevronDown className="w-3 h-3" /> Show expanded detail ({cae.word_count_expanded} words)</>
                  )}
                </button>
                {showExpanded && (
                  <p
                    className="mt-2 text-xs text-slate-700 leading-relaxed bg-slate-50 border border-slate-200 rounded p-2"
                    data-testid={`cae-expanded-${jgId}`}
                  >
                    {cae.expanded}
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* Edit mode */}
        {editing && (
          <div className="space-y-2">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">
                Customer summary ({wordCount(editSummary)} words — aim for 40-90)
              </label>
              <Textarea
                rows={5}
                value={editSummary}
                onChange={e => setEditSummary(e.target.value)}
                placeholder="Primary customer-facing recommendation…"
                data-testid={`cae-edit-summary-${jgId}`}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">
                Expanded detail ({wordCount(editExpanded)} words — aim for 100-180)
              </label>
              <Textarea
                rows={6}
                value={editExpanded}
                onChange={e => setEditExpanded(e.target.value)}
                placeholder="Optional supporting explanation that appears in the 'Read more' accordion on the portal."
                data-testid={`cae-edit-expanded-${jgId}`}
              />
            </div>
          </div>
        )}

        <div className="mt-3 flex gap-2 flex-wrap">
          {!editing && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy || isGenerating}
                onClick={handleRegenerate}
                data-testid={`cae-regenerate-${jgId}`}
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${(busy || isGenerating) ? "animate-spin" : ""}`} />
                {isGenerating ? "Generating…" : (cae?.summary ? "Regenerate" : "Generate")}
              </Button>
              {cae?.summary && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleStartEdit}
                  data-testid={`cae-edit-${jgId}`}
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              )}
              {/* iter198xx — Delete button. Visible whenever there's
                  any non-deleted content; the regenerate button is
                  still visible afterwards so the operation is
                  immediately reversible. */}
              {cae?.summary && !cae?.deleted && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={handleDelete}
                  className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
                  data-testid={`cae-delete-${jgId}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              )}
            </>
          )}
          {editing && (
            <>
              <Button
                type="button"
                size="sm"
                disabled={busy}
                onClick={handleSaveEdit}
                data-testid={`cae-save-edit-${jgId}`}
              >
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setEditing(false)}
                data-testid={`cae-cancel-edit-${jgId}`}
              >
                <X className="w-3 h-3 mr-1" /> Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
